/**
 * The closed lists of the purchases contract (supplier bills and credits,
 * supplier payments, suppliers, the review queue, approvals), of bank accounts and bank
 * movements, and of the ledger's chart, default accounts and stock counts. The server types most as plain
 * strings; here each is a literal union, and every value is seeded EN and AR
 * (`codes.test.ts` holds each list to its bundle keys). An unknown value from
 * the server still renders a generic label.
 */
import type { components } from "@linkhr/api-client/core";

// ---- Supplier documents --------------------------------------------------------------

/** `state.bill.*` — a supplier document's state; there is no APPROVED state (approving posts). */
export const DOC_STATES = [
  "DRAFT",
  "IN_REVIEW",
  "CHANGES_REQUESTED",
  "POSTED",
  "REJECTED",
  "VOID_BEFORE_POSTING",
  "REVERSED",
] as const;

/** `bills.kind.*` — the document's `type`. */
export const DOC_TYPES = ["BILL", "VENDOR_CREDIT"] as const;
export type DocType = (typeof DOC_TYPES)[number];

/** `state.settlement.*` — a posted document's settlement (Overdue is derived from `overdueDays`). */
export const SETTLEMENT_STATES = ["UNPAID", "PARTLY_SETTLED", "SETTLED"] as const;

/** `state.evidence.*` — the e-invoice (JoFotara) axis. */
export const EINVOICE_STATES = ["EINVOICE_PRESENT", "EINVOICE_MISSING", "NOT_APPLICABLE"] as const;
/** `state.qr.*` */
export const QR_STATES = ["DECODED", "STRUCTURALLY_VALID", "CROSS_CHECKED"] as const;
/** `state.tax_invoice.*` — the GST tax-invoice axis. */
export const TAX_INVOICE_STATES = ["PRESENT", "MISSING", "NOT_REQUIRED"] as const;
/** `bills.deductibility.*` — whether the cost counts for income tax. */
export const DEDUCTIBILITY = ["DEDUCTIBLE", "AT_RISK", "NOT_APPLICABLE"] as const;
/** `bills.tax_invoice_reason.*` */
export const TAX_INVOICE_REASONS = ["BUYER_NOT_NAMED"] as const;

/** `bills.check.*` — the checks a draft or a bill in review carries. */
export const CHECK_CODES = [
  "MISSING_REQUIRED",
  "NEW_SUPPLIER",
  "CREDIT_OF_INVALID",
  "TAX_RATE_NOT_EFFECTIVE",
  "ACCOUNT_NOT_POSTABLE",
  "TAX_DIFFERS_FROM_RATE",
  "TOTALS_MISMATCH",
  "DUPLICATE_SUPPLIER_NUMBER",
  "DUPLICATE_NUMBER_DIFFERENT_TOTAL",
  "POSSIBLE_DUPLICATE",
  "PAYEE_BANK_MISMATCH",
  "LOW_CONFIDENCE_FIELD",
  "TAX_EVIDENCE_MISSING",
  "PERIOD_NOT_OPEN",
] as const;
export type CheckCode = (typeof CHECK_CODES)[number];

/**
 * `bills.field.*` — the fields a `MISSING_REQUIRED` check names. Line fields
 * come as `lines.{i}.<field>` (0-based); the label is the `<field>` part.
 * Business view names the account a category (`bills.field.category`).
 */
export const CHECK_FIELDS = [
  "contact",
  "documentDate",
  "dueDate",
  "rate",
  "lines",
  "quantity",
  "unitPrice",
  "statedAmount",
  "taxes",
  "specialTax",
  "accountId",
] as const;

/** `bills.severity.*` */
export const CHECK_SEVERITIES = ["BLOCKING", "ADVISORY"] as const;
/** `bills.resolution.*` — what a check asks of the person. */
export const CHECK_RESOLUTIONS = [
  "CONFIRM",
  "DISMISS_WITH_REASON",
  "ACKNOWLEDGE",
  "EDIT",
  "NONE",
] as const;
/** `bills.check_source.*` */
export const CHECK_SOURCES = ["AI", "RULE", "PERSON"] as const;
/** `bills.duplicate_rule.*` — why `POSSIBLE_DUPLICATE` fired (`args.rule`). */
export const DUPLICATE_RULES = [
  "NUMBER_OTHER_YEAR",
  "AMOUNT_AND_DATE",
  "NUMBER_AND_AMOUNT_OTHER_SUPPLIER",
  "SAME_FILE",
] as const;
/** `bills.blocked_by.*` — why the viewer can't approve (`approval.viewer.blockedBy`). */
export const APPROVE_BLOCKED_BY = ["NOT_PERMITTED", "CHECKS_OPEN", "PRODUCT_READ_ONLY"] as const;
/** `bills.empty_reason.*` / `suppliers.empty_reason.*` */
export const EMPTY_REASONS = ["NO_APPROVER"] as const;
/** `bills.credit_of_invalid.*` — the `CREDIT_OF_INVALID` check's `args.reason`. */
export const CREDIT_OF_INVALID_REASONS = [
  "NOT_POSTED",
  "SUPPLIER_MISMATCH",
  "CURRENCY_MISMATCH",
  "EXCEEDS_BILL",
  "EXCEEDS_PENDING_GST",
  "PENDING_GST_OPEN",
] as const;
/** `bills.reject_reason.*` */
export const REJECT_REASONS = ["DUPLICATE", "NOT_OURS", "WRONG_AMOUNT", "OTHER"] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];
/** `bills.void_reason.*` */
export const VOID_REASONS = ["DUPLICATE", "ENTERED_IN_ERROR", "NOT_OURS", "OTHER"] as const;
export type VoidReason = (typeof VOID_REASONS)[number];
/** `bills.batch.outcome.*` */
export const BATCH_OUTCOMES = ["ALL_APPROVED", "PARTIAL", "NONE"] as const;
/** `bills.batch.item.*` */
export const BATCH_ITEM_OUTCOMES = ["APPROVED", "STALE", "BLOCKED", "INVALID"] as const;
/** `bills.effect.*.{business,accountant}` — what approving does, in each view's words. */
export const EFFECT_KINDS = [
  "OWE_SUPPLIER",
  "COST",
  "TAX_RECOVERABLE",
  "TAX_PENDING",
  "TAX_NOT_RECOVERABLE",
] as const;
/** `bills.origin.*` — where an AI-read field's value came from. */
export const PROVENANCE_ORIGINS = ["DOCUMENT", "INFERRED", "CALCULATED", "RULE"] as const;
/** `bills.actor.*` */
export const ACTOR_KINDS = ["PERSON", "AI", "RULE", "SYSTEM"] as const;
/** `bills.activity.*` — a document's or a work item's activity verbs (unknown → `bills.activity_other`). */
export const ACTIVITY_VERBS = [
  "AI_DRAFTED",
  "CREATED",
  "EDITED",
  "SUBMITTED",
  "CHECK_CONFIRMED",
  "CHECK_DISMISSED",
  "CHECK_ACKNOWLEDGED",
  "APPROVED",
  "POSTED",
  "CHANGES_REQUESTED",
  "REJECTED",
  "VOIDED",
  "REVERSED",
  "ATTACHED",
  "HELD",
  "HOLD_RELEASED",
  "CREDIT_APPLIED",
  "CREDIT_UNAPPLIED",
  "EVIDENCE_ADDED",
  "TAX_RECLASSIFIED",
  "QUESTION_ASKED",
  "ANSWERED",
  "ASSIGNED",
  "RESOLVED",
  "REOPENED",
  // A payment's own, then what a payment does to a bill.
  "APPROVAL_CLEARED",
  "APPROVAL_RENEWED",
  "RELEASED",
  "IMPORTED_TAX_SETTLED",
  "PAYMENT_RELEASED",
  "PAYMENT_REVERSED",
  "ADVANCE_APPLIED",
  "ADVANCE_UNAPPLIED",
  "WRITTEN_OFF",
  "WRITE_OFF_DECLINED",
  "WRITE_OFF_REVERSED",
] as const;
/**
 * `payments.activity.*.{business,accountant}` — a payment's verbs the bill wording doesn't fit
 * (a voided payment stays listed; a payment is approved in the payments' own word).
 */
export const PAYMENT_ACTIVITY_VERBS = ["VOIDED", "APPROVED"] as const;

/** `bills.payment_kind.*` — a row of a bill's Payments tab. */
export const BILL_PAYMENT_KINDS = ["PAYMENT", "ADVANCE_APPLIED"] as const;
/** `bills.write_off_request_state.*` — a request to write off the rest of a bill, as its item ends. */
export const WRITE_OFF_REQUEST_STATES = [
  "OPEN",
  "WRITTEN_OFF",
  "PAID",
  "DECLINED",
  "ENDED",
] as const;
/** `bills.action_blocked_by.*` — why a posted bill's write-off, decline or reverse is shut (≠ `APPROVE_BLOCKED_BY`). */
export const BILL_ACTION_BLOCKED_BY = [
  "NOT_PERMITTED",
  "PRODUCT_READ_ONLY",
  "NOT_A_BILL",
  "NOT_POSTED",
  "FOREIGN_CURRENCY",
  "NOTHING_REMAINING",
  "NOTHING_REQUESTED",
  "PAID_BY_PAYMENT",
  "CREDITS_APPLIED",
  "ADVANCE_APPLIED",
] as const;

// ---- Supplier payments ---------------------------------------------------------------

/** `state.supplier_payment.*` — CLEARED is written by bank matching only. */
export const SUPPLIER_PAYMENT_STATES = [
  "DRAFT",
  "APPROVED",
  "RELEASED",
  "CLEARED",
  "VOIDED",
  "REVERSED",
] as const;
export type SupplierPaymentState = (typeof SUPPLIER_PAYMENT_STATES)[number];
/** `method.*` */
export const SUPPLIER_PAYMENT_METHODS = ["BANK_TRANSFER", "CLIQ", "CHEQUE", "CASH"] as const;
export type SupplierPaymentMethod = (typeof SUPPLIER_PAYMENT_METHODS)[number];
/** `payments.kind.*` — what an allocation row pays or uses. */
export const ALLOCATION_KINDS = ["BILL", "PAYABLE", "CREDIT", "ADVANCE"] as const;
export type AllocationKind = (typeof ALLOCATION_KINDS)[number];
/** `payments.page.rest.*.{business,accountant}` — what happens to the rest of a bill paid in part. */
export const REMAINDER_CHOICES = ["KEEP_OPEN", "REQUEST_WRITE_OFF"] as const;
/** `payments.write_off_reason.*` — a write-off's reason, asked on a payment or posted on a bill. */
export const WRITE_OFF_REASONS = [
  "DISCOUNT_AGREED",
  "SUPPLIER_WAIVER",
  "SMALL_DIFFERENCE",
  "OTHER",
] as const;
/** `payments.void_reason.*` (≠ the bills' `VOID_REASONS`: NOT_NEEDED, not NOT_OURS). */
export const PAYMENT_VOID_REASONS = [
  "DUPLICATE",
  "ENTERED_IN_ERROR",
  "NOT_NEEDED",
  "OTHER",
] as const;
/** `payments.reverse_reason.*` — STOPPED is for a lost cheque or a bankrupt holder only. */
export const PAYMENT_REVERSE_REASONS = [
  "CANCELLED",
  "STOPPED",
  "ENTERED_IN_ERROR",
  "OTHER",
] as const;
/** `payments.ended_reason.*` — a voided or reversed payment's `ended.reason`. */
export const PAYMENT_ENDED_REASONS = [
  "DUPLICATE",
  "ENTERED_IN_ERROR",
  "NOT_NEEDED",
  "OTHER",
  "CANCELLED",
  "STOPPED",
] as const;
/** `payments.write_off_reverse_reason.*` — undoing a bill's write-off. */
export const WRITE_OFF_REVERSE_REASONS = ["ENTERED_IN_ERROR", "OTHER"] as const;
/** `payments.rate_source.*` */
export const RATE_SOURCES = ["RATE_TABLE", "ENTERED"] as const;
/** `payments.amount_source.*` — an amount typed in, or the rows' total. */
export const AMOUNT_SOURCES = ["ENTERED", "FROM_ROWS"] as const;
/** `payments.payable_source.*` — where a PAYABLE row's amount owed came from. */
export const PAYABLE_SOURCE_TYPES = ["OPENING_BALANCE", "MANUAL_JOURNAL"] as const;
/** `payments.fx_mode.*` */
export const FX_MODES = ["NONE", "REALISED_AT_RELEASE", "AT_BANK_MATCH"] as const;
/** `payments.fx_direction.*` */
export const FX_DIRECTIONS = ["GAIN", "LOSS"] as const;
/** `bills.page.history.entry.*` — the entries a bill or a supplier credit posts (its Journal items). */
export const BILL_ENTRY_EFFECTS = [
  "BILL_RECOGNIZED",
  "VENDOR_CREDIT_RECOGNIZED",
  "PENDING_TAX_RESOLVED",
] as const;
/** `payments.entry_effect.*` — the entries a payment posts (the release preview, an entry's page). */
export const PAYMENT_ENTRY_EFFECTS = [
  "SUPPLIER_PAYMENT_RELEASED",
  "IMPORTED_SERVICE_TAX_DUE",
  "IMPORTED_SERVICE_TAX_SETTLED",
  "PAYABLE_WRITTEN_OFF",
] as const;
/** `payments.blocked_by.*` — why the viewer can't act, in the server's order (the first that applies). */
export const PAYMENT_BLOCKED_BY = [
  "NOT_PERMITTED",
  "PRODUCT_READ_ONLY",
  "WRONG_STATE",
  "DUAL_CONTROL_EDITOR",
  "CHECKS_OPEN",
  "APPROVAL_OUTDATED",
  "NOT_APPROVED",
  "ADVANCE_USED",
  "NO_IMPORTED_TAX",
] as const;
/** `payments.edit_effect.*` — what saving costs (edit only). */
export const EDIT_EFFECTS = [
  "CLEARS_APPROVAL",
  "VIEWER_LOSES_APPROVE",
  "VIEWER_LOSES_RELEASE",
] as const;
/** `payments.empty_reason.*` — why nobody may approve or release (also a list row's `stuck.reason`). */
export const PAYMENT_EMPTY_REASONS = ["NOBODY_HOLDS_CODE", "ONLY_EDITORS_HOLD"] as const;
/** `payments.next_step.*` — the way out when only the payment's editors hold the code. */
export const PAYMENT_NEXT_STEPS = [
  "TURN_OFF_DUAL_CONTROL_THEN_PREPARE_AGAIN",
  "ASK_ANOTHER_PREPARER",
  "TURN_OFF_DUAL_CONTROL",
] as const;
/** `payments.waiting_for.*` */
export const WAITING_FOR = ["APPROVAL", "RELEASE"] as const;
/** `payments.stuck.*` — a list row nobody can move on. */
export const STUCK_ACTIONS = ["APPROVE", "RELEASE"] as const;
/**
 * `payments.check.*` — the documented payment checks. An open list: an unknown
 * code reads a generic label.
 */
export const PAYMENT_CHECK_CODES = [
  "NO_PAYABLES",
  "NOTHING_TO_PAY",
  "APPLIED_EXCEEDS_CASH",
  "EXCEEDS_REMAINING",
  "DOCUMENT_NOT_PAYABLE",
  "BILL_ON_HOLD",
  "DOCUMENT_AFTER_PAYMENT_DATE",
  "PAYMENT_DATE_IN_FUTURE",
  "PAYEE_NOT_VERIFIED",
  "PAYEE_CHANGE_PENDING",
  "PAYEE_CHANGED",
  "CHEQUE_FIELDS_MISSING",
  "CHEQUE_DATE_BEFORE_PAYMENT",
  "CHEQUE_NUMBER_INVALID",
  "CHEQUE_NUMBER_IN_USE",
  "CURRENCY_MIXED",
  "CURRENCY_NOT_SUPPORTED",
  "PAY_FROM_INVALID",
  "FX_RATE_MISSING",
  "FOREIGN_ADVANCE",
  "CREDIT_OR_ADVANCE_FOREIGN",
  "ADVANCE_ON_IMPORTED_SERVICE",
  "WRITE_OFF_NOT_ALLOWED",
  "POSTING_REFUSED",
  "PERIOD_NOT_OPEN",
  "ALSO_IN_PAYMENT",
  "LARGE_ADVANCE",
  "PAYMENT_REVERSED_ON_BILL",
] as const;
/** `payments.pay_from_invalid.*` — the `PAY_FROM_INVALID` check's `args.reason`. */
export const PAY_FROM_INVALID_REASONS = [
  "NOT_BANK",
  "NOT_CASH",
  "CURRENCY",
  "CLIQ_NOT_JOD",
  "INACTIVE",
  "METHOD_CURRENCY",
  "RETIRED",
] as const;
/** `payments.write_off_check.*` — the `WRITE_OFF_NOT_ALLOWED` check's `args.reason`. */
export const WRITE_OFF_CHECK_REASONS = ["NOT_A_BILL", "FOREIGN_CURRENCY"] as const;
/** `payments.changed.*` — `changedFields` and an EDITED activity row's `fieldPath`. */
export const PAYMENT_CHANGED_FIELDS = [
  "contactId",
  "method",
  "payFromAccountId",
  "paymentDate",
  "currency",
  "amount",
  "rate",
  "rateSource",
  "chequeNumber",
  "chequeDate",
  "reference",
  "note",
  "payee",
  "state",
  "allocations",
] as const;
/** `payments.dual_control_role.*` — `ACCT_PAYMENT_DUAL_CONTROL.role`. */
export const DUAL_CONTROL_ROLES = ["APPROVER", "RELEASER"] as const;
/** `payments.action.*` — a refusal's `action` (`WRONG_STATE`, `DUAL_CONTROL`). */
export const PAYMENT_ACTIONS = [
  "EDIT",
  "APPROVE",
  "RELEASE",
  "VOID",
  "REVERSE",
  "SETTLE_IMPORTED_TAX",
] as const;

// ---- Suppliers, payee bank, tax codes, approvals ---------------------------------------

/** `suppliers.kind.*` — upper case on the wire (lower case is a 400). */
export const CONTACT_KINDS = ["SUPPLIER", "CUSTOMER", "OTHER"] as const;
/** `suppliers.status.*` */
export const CONTACT_STATUSES = ["ACTIVE", "ARCHIVED"] as const;
/** `suppliers.source.*` */
export const CONTACT_SOURCES = ["PERSON", "AI_DRAFT"] as const;
/** `suppliers.confirmation.*` */
export const CONTACT_CONFIRMATIONS = ["CONFIRMED", "UNCONFIRMED"] as const;
/** `suppliers.alias_source.*` */
export const ALIAS_SOURCES = ["PERSON", "AI", "DOCUMENT"] as const;
/** `state.payee_change.*` */
export const PAYEE_CHANGE_STATES = ["PROPOSED", "APPROVED", "REJECTED", "WITHDRAWN"] as const;
/** `suppliers.blocked_by.*` — why the viewer can't decide a change (`viewer.blockedBy`). */
export const PAYEE_BLOCKED_BY = [
  "NOT_PENDING",
  "NOT_PERMITTED",
  "PROPOSED_IT_DUAL_CONTROL",
] as const;
/** `suppliers.next_step.*` */
export const PAYEE_NEXT_STEPS = ["WITHDRAW_AND_PROPOSE_AGAIN"] as const;
/** `suppliers.tax_kind.*` */
export const TAX_KINDS = [
  "STANDARD",
  "ZERO_RATED",
  "EXEMPT",
  "OUT_OF_SCOPE",
  "SPECIAL",
  "IMPORTED_SERVICE",
  "BLOCKED",
] as const;
/** `suppliers.tax_step.*` — 1 = special tax (first), 2 = GST. */
export const TAX_STEPS = ["1", "2"] as const;
/** `suppliers.bank_status.*` */
export const BANK_STATUSES = ["ACTIVE", "RETIRED"] as const;
/** `approvals.member_source.*` */
export const MEMBER_SOURCES = ["OWNER", "GRANT"] as const;
/** `approvals.history_field.*` */
export const SETTINGS_FIELDS = ["dualControl", "defaultReviewerUserId"] as const;

// ---- Review queue ---------------------------------------------------------------------

/** `state.work_item.*` */
export const WORK_ITEM_STATUSES = ["OPEN", "RESOLVED"] as const;
export type WorkItemStatus = (typeof WORK_ITEM_STATUSES)[number];

/** `queue.family.*` */
export const WORK_ITEM_FAMILIES = [
  "DUPLICATE",
  "PAYEE",
  "REVIEW",
  "QUESTION",
  "TAX",
  "MONEY",
] as const;
export type WorkItemFamily = (typeof WORK_ITEM_FAMILIES)[number];

/** `queue.reason.*.{name,sentence}` — why an item is open. */
export const WORK_ITEM_REASONS = [
  "POSSIBLE_DUPLICATE",
  "PAYEE_BANK_MISMATCH",
  "PAYEE_BANK_CHANGE_PENDING",
  "PAYEE_BANK_RETIRED",
  "CHANGES_REQUESTED",
  "QUESTION_FOR_ACCOUNTANT",
  "QUESTION_FOR_OWNER",
  "TAX_EVIDENCE_MISSING",
  "IMPORTED_SERVICE_TAX_DUE",
  "SHORT_PAYMENT_WRITE_OFF",
  "OWNER_EASY_ENTRY",
] as const;
export type WorkItemReason = (typeof WORK_ITEM_REASONS)[number];

/** `queue.source.*` */
export const WORK_ITEM_SOURCES = ["RULE", "PERSON", "AI"] as const;
/** `queue.subject.*` — an open list: an unknown type reads `queue.subject_other`. */
export const WORK_ITEM_SUBJECTS = [
  "DOCUMENT",
  "JOURNAL",
  "PAYEE_BANK_CHANGE",
  "PAYMENT",
  "BANK_MOVEMENT",
] as const;
/**
 * `queue.resolution.*` — the server's resolution words and our own two (we send
 * DONE or NOT_AN_ISSUE). Any other value is a person's text: render it raw.
 */
export const RESOLUTIONS = [
  "POSTED",
  "CLEARED",
  "ENDED",
  "RESUBMITTED",
  "RECOVERED",
  "EXPENSED",
  "APPROVED",
  "REJECTED",
  "WITHDRAWN",
  "SETTLED",
  "WRITTEN_OFF",
  "PAID",
  "DECLINED",
  "DONE",
  "NOT_AN_ISSUE",
] as const;

// ---- Bank accounts and bank movements ---------------------------------------------------

type BankAccountType = components["schemas"]["AcctBankAccount"]["type"];
type MovementRequest = components["schemas"]["AcctBankMovementRequest"];

/** `banking.account_type.*` — cash accounts are read here, never added. */
export const BANK_ACCOUNT_TYPES = [
  "BANK",
  "WALLET",
  "CASH",
] as const satisfies readonly BankAccountType[];
/** `easy.type.*` (the owner's tile) and, with a category, `banking.kind.*` (`movementName`). */
export const MOVEMENT_KINDS = [
  "OWNER_IN",
  "OWNER_OUT",
  "MONEY_IN",
  "MONEY_OUT",
  "TRANSFER",
] as const satisfies readonly MovementRequest["kind"][];
export type MovementKind = (typeof MOVEMENT_KINDS)[number];
/** `banking.category.{kind}.*` — the categories other money in or out takes; any other pair is refused. */
export const MOVEMENT_CATEGORIES_BY_KIND = {
  MONEY_IN: ["INTEREST", "OTHER"],
  MONEY_OUT: ["BANK_CHARGES", "INTEREST", "OTHER"],
} as const;
/** `state.bank_movement.*.{business,accountant}` */
export const MOVEMENT_STATES = [
  "DRAFT",
  "SENT",
  "POSTED",
  "VOIDED",
  "REVERSED",
] as const satisfies readonly components["schemas"]["AcctBankMovement"]["state"][];
/** `banking.blocked_by.*` — why the viewer can't act on an account or a movement. */
export const BANK_BLOCKED_BY = [
  "NOT_PERMITTED",
  "PRODUCT_READ_ONLY",
  "WRONG_STATE",
  "NOT_RECONCILABLE",
  "ACCOUNT_RETIRED",
  "NOT_IN_TRANSIT",
  "MATCHED",
] as const satisfies readonly NonNullable<components["schemas"]["AcctBankAction"]["blockedBy"]>[];

/** `banking.kind.*` — a movement's name (titles, list columns, the home): its kind, or the category of other money. */
export const MOVEMENT_NAMES = [
  "OWNER_IN",
  "OWNER_OUT",
  "TRANSFER",
  "MONEY_IN_INTEREST",
  "MONEY_IN_OTHER",
  "MONEY_OUT_BANK_CHARGES",
  "MONEY_OUT_INTEREST",
  "MONEY_OUT_OTHER",
] as const;
export type MovementName = (typeof MOVEMENT_NAMES)[number];

/** The name of a movement of `kind`; other money in or out with no category it takes reads as OTHER. */
export function movementName(
  kind: MovementKind,
  category: string | null | undefined,
): MovementName {
  if (kind !== "MONEY_IN" && kind !== "MONEY_OUT") return kind;
  const taken: readonly string[] = MOVEMENT_CATEGORIES_BY_KIND[kind];
  return `${kind}_${category != null && taken.includes(category) ? category : "OTHER"}` as MovementName;
}

// ---- Refusals the screens word themselves ---------------------------------------------

/** `suppliers.error.*` */
export const SUPPLIER_ERROR_CODES = [
  "ACCT_PAYEE_CHANGE_OPEN",
  "ACCT_PAYEE_CHANGE_NOT_PENDING",
  "ACCT_PAYEE_CHANGE_SELF_APPROVAL",
  "ACCT_IBAN_INVALID",
  "CONCURRENT_MODIFICATION",
  "STEP_UP_PROOF_USED",
] as const;
/** `queue.error.*` */
export const QUEUE_ERROR_CODES = ["ACCT_WORK_ITEM_RESOLVED", "ACCT_WORK_ITEM_RULE_OWNED"] as const;
/** `approvals.error.*` */
export const APPROVALS_ERROR_CODES = [
  "ACCT_SETTINGS_VERSION_CONFLICT",
  "STEP_UP_PROOF_USED",
] as const;

/**
 * `payments.stale.*` — why a payment's approve, release or save met a newer version:
 * EDITED and BOOKS_MOVED (`ACCT_PAYMENT_STALE`), APPROVED (`ACCT_DRAFT_STALE`).
 */
export const PAYMENT_STALE_REASONS = ["EDITED", "BOOKS_MOVED", "APPROVED"] as const;

// ---- The chart, default accounts and stock counts ---------------------------------------

type AcctRole = components["schemas"]["AcctRole"];

/** `ledger.account_roles.*` — the default accounts (account roles) of the catalogue, in the contract's order. */
export const ACCT_ROLES = [
  "CASH_ON_HAND",
  "PETTY_CASH",
  "BANK",
  "BANK_FOREIGN",
  "INTERNAL_TRANSFERS",
  "TRADE_RECEIVABLES",
  "ALLOWANCE_FOR_DOUBTFUL_DEBTS",
  "PDC_RECEIVED",
  "PDC_UNDER_COLLECTION",
  "CHEQUES_UNDER_DISCOUNT",
  "CUSTODY_RECEIVABLE",
  "EMPLOYEE_ADVANCES",
  "SUPPLIER_ADVANCES",
  "INTERCOMPANY_RECEIVABLE",
  "INPUT_TAX",
  "WHT_RECEIVABLE",
  "PREPAID_EXPENSES",
  "INVENTORY",
  "LG_CASH_MARGIN",
  "PROPERTY_PLANT_EQUIPMENT",
  "ACCUMULATED_DEPRECIATION",
  "TRADE_PAYABLES",
  "GOODS_RECEIVED_NOT_INVOICED",
  "PDC_ISSUED",
  "CUSTOMER_ADVANCES",
  "DEFERRED_REVENUE",
  "ACCRUED_EXPENSES",
  "INTERCOMPANY_PAYABLE",
  "BANK_BORROWINGS",
  "OUTPUT_TAX",
  "VAT_PAYABLE",
  "WHT_PAYABLE",
  "INCOME_TAX_WITHHELD_PAYABLE",
  "INCOME_TAX_PAYABLE",
  "ZAKAT_PROVISION",
  "NET_SALARIES_PAYABLE",
  "SSC_PAYABLE",
  "GOSI_PAYABLE",
  "LEAVE_PROVISION",
  "EOS_PROVISION",
  "DIVIDENDS_PAYABLE",
  "SHARE_CAPITAL",
  "OWNER_DRAWINGS",
  "STATUTORY_RESERVE",
  "RETAINED_EARNINGS",
  "INCOME_SUMMARY",
  "OPENING_BALANCE_SUSPENSE",
  "SALES_REVENUE",
  "SALES_RETURNS",
  "INTEREST_INCOME",
  "MANAGEMENT_FEE_INCOME",
  "BAD_DEBTS_RECOVERED",
  "FX_GAIN_LOSS",
  "DISPOSAL_GAIN_LOSS",
  "COST_OF_GOODS_SOLD",
  "PURCHASES",
  "INVENTORY_LOSS",
  "INVENTORY_WRITE_DOWN",
  "GENERAL_EXPENSES",
  "PROFESSIONAL_FEES",
  "MANAGEMENT_FEE_EXPENSE",
  "CASH_SHORTAGE",
  "BANK_CHARGES",
  "FINANCE_COSTS",
  "DEPRECIATION_EXPENSE",
  "IMPAIRMENT_LOSS_ON_RECEIVABLES",
  "SALARIES_EXPENSE",
  "ALLOWANCES_EXPENSE",
  "EMPLOYER_SOCIAL_INSURANCE_EXPENSE",
  "EOS_EXPENSE",
  "LEAVE_EXPENSE",
  "ZAKAT_EXPENSE",
  "INCOME_TAX_EXPENSE",
  "ROUNDING_DIFFERENCE",
  "OUTSTANDING_PAYMENTS",
  "OUTSTANDING_RECEIPTS",
  "INPUT_TAX_PENDING",
  "DISCOUNT_RECEIVED",
  "IMPORTED_SERVICES_TAX_PAYABLE",
  "BANK_SUSPENSE",
] as const satisfies readonly AcctRole[];

/** Compiles only while `ACCT_ROLES` lists every role the contract has (`satisfies` proves the other direction). */
export const ACCT_ROLES_COMPLETE: Exclude<AcctRole, (typeof ACCT_ROLES)[number]> extends never
  ? true
  : never = true;

/** `ledger.entry.source.*` — what posted an entry, and what an account's `allowedSources` can list. */
export const ENTRY_SOURCES = [
  "MANUAL_JOURNAL",
  "OPENING_BALANCE",
  "SUPPLIER_BILL",
  "SUPPLIER_CREDIT",
  "SUPPLIER_PAYMENT",
  "INVENTORY_COUNT",
  "BANK_ADJUSTMENT",
  "BANK_TRANSFER",
] as const;

/** `ACCT_ACCOUNT_CHANGE_BLOCKED`: the reasons each change can be refused for. */
export const ACCOUNT_CHANGE_BLOCKED_REASONS = {
  ADD_CHILD: ["HAS_LINES", "ROLE_MAPPED", "CONTROL", "RESTRICTED", "RETIRED"],
  MOVE: ["ROOT", "RESTRICTED", "BANK_ACCOUNT", "DIMENSION_RULE"],
  RETIRE: ["HAS_LATER_LINES", "ROLE_MAPPED", "GROUP", "BEFORE_ACTIVE_FROM"],
  UNGROUP: ["HAS_CHILDREN", "ROOT"],
} as const;
/** `ACCT_REQUIRED_DIMENSION_NOT_ALLOWED.reason` */
export const REQUIRED_DIMENSION_REASONS = ["NOT_PROFIT_AND_LOSS", "ENGINE_ACCOUNT"] as const;
/** `ACCT_ROLE_LOCKED.reason`, and a default account's `locked`. */
export const ROLE_LOCKED_REASONS = ["SOURCE_RESTRICTED", "QUALIFIED"] as const;
/** `ACCT_ROLE_MAPPING_INVALID.reason` */
export const ROLE_MAPPING_INVALID_REASONS = [
  "CLASS_MISMATCH",
  "ACCOUNT_IS_GROUP",
  "ACCOUNT_INACTIVE",
  "ACCOUNT_HAS_ROLE",
  "ACCOUNT_RESTRICTED",
  "ACCOUNT_CURRENCY",
  "ACCOUNT_HAS_BALANCE",
  "DIMENSION_RULE",
  "DATE",
] as const;
/** `ACCT_ROLE_ACCOUNT_INVALID.reason` (the database's guard; scripts only). */
export const ROLE_ACCOUNT_INVALID_REASONS = ["SOURCES", "CLASS", "CONTROL"] as const;
/** `ACCT_INVENTORY_COUNT_INVALID.reason` */
export const INVENTORY_COUNT_REASONS = [
  "NO_DIFFERENCE",
  "ACCOUNT_INVALID",
  "CONTRA_INVALID",
  "DATE_IN_FUTURE",
  "BEFORE_LATEST_COUNT",
] as const;

// ---- Screen tabs (the routes' `?tab=` search params) -----------------------------------

/** The supplier bills list. */
export const BILL_LIST_TABS = [
  "all",
  "drafts",
  "sent_back",
  "waiting",
  "unpaid",
  "overdue",
  "waiting_bank",
  "paid",
  "missing_tax_invoice",
  "no_einvoice",
] as const;
export type BillListTab = (typeof BILL_LIST_TABS)[number];

/** The supplier credits list. */
export const CREDIT_LIST_TABS = ["all", "drafts", "in_review", "open", "used"] as const;
export type CreditListTab = (typeof CREDIT_LIST_TABS)[number];

/** A bill's or a credit's page. */
export const DOC_PAGE_TABS = ["details", "payments", "journal", "files", "activity"] as const;
export type DocPageTab = (typeof DOC_PAGE_TABS)[number];

/** The supplier payments list: by where each payment stands. */
export const PAYMENT_LIST_TABS = [
  "for_you",
  "drafts",
  "approved",
  "waiting_bank",
  "released",
  "cleared",
  "ended",
  "all",
] as const;
export type PaymentListTab = (typeof PAYMENT_LIST_TABS)[number];

/** A supplier payment's page. */
export const PAYMENT_PAGE_TABS = ["details", "journal", "activity"] as const;
export type PaymentPageTab = (typeof PAYMENT_PAGE_TABS)[number];

/** The bank movements list (`banking.tab.*.{business,accountant}`); `movementTabQuery` maps each to its filter. */
export const MOVEMENT_LIST_TABS = [
  "waiting",
  "drafts",
  "in_transit",
  "posted",
  "ended",
  "all",
] as const;
export type MovementListTab = (typeof MOVEMENT_LIST_TABS)[number];
